import React, { useState } from "react";
import {
  Page,
  Card,
  Layout,
  FormLayout,
  TextField,
  Button,
  Banner,
  Spinner,
} from "@shopify/polaris";
import axios from "../axiosInstance";

const MIN_DATE = "2026-01-01";

function toISODate(date) {
  const year = date.getFullYear();
  const mm = String(date.getMonth() + 1).padStart(2, "0");
  const dd = String(date.getDate()).padStart(2, "0");
  return `${year}-${mm}-${dd}`;
}

export default function RecycleFeeReport() {
  const now = new Date();
  const TODAY = toISODate(now);

  const [startDate, setStartDate] = useState(MIN_DATE);
  const [endDate, setEndDate] = useState(TODAY);
  const [loading, setLoading] = useState(false);
  const [status, setStatus] = useState({ error: false, message: "" });

  const validate = () => {
    if (!startDate) return "Start date is required.";
    if (!endDate) return "End date is required.";
    if (startDate < MIN_DATE) return `Start date cannot be before ${MIN_DATE}.`;
    if (endDate > TODAY) return "End date cannot be in the future.";
    if (startDate > endDate) return "Start date must be before or equal to end date.";
    return null;
  };

  const handleGenerateReport = async () => {
    const validationError = validate();
    if (validationError) {
      return setStatus({ error: true, message: validationError });
    }

    setLoading(true);
    setStatus({ error: false, message: "" });

    const payload = { mode: "custom", startDate, endDate };

    try {
      const { data } = await axios.post("/api/recycleFee/report", payload);

      if (data?.status) {
        setStatus({ error: false, message: data.msg });
      } else {
        setStatus({
          error: true,
          message: data?.msg || "Failed to initiate report generation.",
        });
      }
    } catch (error) {
      console.error("Error generating recycle fee report:", error);

      const errorMsg =
        error?.response?.data?.msg ||
        error?.response?.data?.errMsg ||
        error?.message ||
        "An unexpected error occurred.";

      setStatus({ error: true, message: errorMsg });
    } finally {
      setLoading(false);
    }
  };

  return (
    <Page
      breadcrumbs={[{ url: "/" }]}
      title="California Recycle Fee Report"
      fullWidth
    >
      <Layout>
        <Layout.Section>
          <Card sectioned>
            <FormLayout>
              <FormLayout.Group>
                <TextField
                  label="Start Date"
                  type="date"
                  value={startDate}
                  onChange={setStartDate}
                  min={MIN_DATE}
                  max={TODAY}
                  disabled={loading}
                  requiredIndicator
                  autoComplete="off"
                />
                <TextField
                  label="End Date"
                  type="date"
                  value={endDate}
                  onChange={setEndDate}
                  min={MIN_DATE}
                  max={TODAY}
                  disabled={loading}
                  requiredIndicator
                  autoComplete="off"
                />
              </FormLayout.Group>

              <Button
                primary
                onClick={handleGenerateReport}
                loading={loading}
                disabled={loading}
              >
                Generate Report
              </Button>
            </FormLayout>
          </Card>
        </Layout.Section>

        {status.message && (
          <Layout.Section>
            <Banner
              title={status.error ? "Error" : "Success"}
              status={status.error ? "critical" : "success"}
            >
              <p>{status.message}</p>
            </Banner>
          </Layout.Section>
        )}

        {loading && (
          <Layout.Section>
            <div
              style={{
                display: "flex",
                justifyContent: "center",
                alignItems: "center",
                marginTop: "20px",
              }}
            >
              <Spinner size="large" accessibilityLabel="Generating report..." />
              <span style={{ marginLeft: "10px" }}>
                Processing can take a few minutes...
              </span>
            </div>
          </Layout.Section>
        )}
      </Layout>
    </Page>
  );
}
